var AbstractImageViewer = require("app/MediaPlayer/Tech/Abstract/AbstractImageViewer"),
    CanvasView          = require("./CanvasView"),
    Control             = require("enyo/Control"),
    IconButton          = require("app/ui/IconButton"),
    Image               = require("./Image"),
    LongDescription     = require("./LongDescription"),
    OverlayLeft         = require("./OverlayLeft"),
    OverlayRight        = require("./OverlayRight"),
    Signals             = require("enyo/Signals"),
    TapCommandSupport   = require("app/ui/TapCommandSupport"),
    Toolbar             = require("./Toolbar"),
    config              = require("app/config"),
    frameThrottle       = require("enyo-perf/utils").frameThrottle,
    get                 = require("object-path").get,
    icons               = require("app/theme/icons"),
    kind                = require("enyo/kind");

require("./ImageViewer.less");
require("app/theme/IconButton--slider.less");

module.exports = kind(/** @lends  app.MediaPlayer.Tech.ImageViewer.prototype */ {

    /**
     * @private
     */
    name: "app.MediaPlayer.Tech.ImageViewer",

    /**
     * @private
     */
    kind: AbstractImageViewer,

    /**
     * @private
     */
    classes: "app-media-player-image-viewer enyo-fit",

    /**
     * @private
     */
    supportsSafeArea: true,

    /**
     * @private
     */
    isReusable: true,

    /**
     * The state of this image.
     *
     * @type  {?Object}
     * @public
     */
    state: null,

    /**
     * Whether to show the fotocadeau cta.
     *
     * @type  {bool}
     * @readonly
     * @public
     */
    showFotocadeauCta: null,

    /**
     * Whether to show the description.
     *
     * @type  {bool}
     * @readonly
     * @public
     */
    showDescription: null,

    /**
     * Whether to use a short description overlay.
     *
     * @type  {bool}
     * @readonly
     * @public
     */
    useOverlayLeft: null,

    /**
     * @private
     */
    components: [{
        kind:    Signals,
        onkeyup: "handleKeyUp"
    }, {
        name:      "canvasView",
        kind:      CanvasView,
        classes:   "app-media-player-image-viewer__canvas-view",
        imageKind: Image
    }, {
        name:    "toolbar",
        kind:    Toolbar,
        classes: "app-media-player-image-viewer__toolbar"
    }, {
        name:       "previousImageButton",
        kind:       IconButton,
        classes:    "app-media-player-image-viewer__previous-image-button",
        mixins:     [TapCommandSupport],
        theme:      "slider",
        icon:       icons["app.MediaPlayer.ImageViewer.previousImageButton"],
        tapCommand: "onRequestPreviousImage"
    }, {
        name:       "nextImageButton",
        kind:       IconButton,
        classes:    "app-media-player-image-viewer__next-image-button",
        mixins:     [TapCommandSupport],
        theme:      "slider",
        icon:       icons["app.MediaPlayer.ImageViewer.nextImageButton"],
        tapCommand: "onRequestNextImage"
    }, {
        name:    "overlayLeft",
        kind:    OverlayLeft,
        classes: "app-media-player-image-viewer__overlay-left"
    }, {
        name:    "overlayRight",
        kind:    OverlayRight,
        classes: "app-media-player-image-viewer__overlay-right"
    }],

    /**
     * @private
     */
    bindings: [{
        from: ".state",
        to:   ".$.toolbar.state"
    }, {
        from: ".fullscreen",
        to:   ".$.toolbar.fullscreen"
    }, {
        from: ".$.canvasView",
        to:   ".$.toolbar.canvasView"
    }, {
        from: ".$.canvasView.activeImage",
        to:   ".activeImage"
    }, {
        from: "showDescription",
        to:   ".$.toolbar.isDescriptionShowing"
    }, {
        from: "showFotocadeauCta",
        to:   ".$.overlayLeft.showFotocadeauCta"
    }, {
        from: "showDescription",
        to:   ".$.overlayLeft.showDescription"
    }, {
        from: "useOverlayLeft",
        to:   ".$.overlayLeft.useOverlayLeft"
    }, {
        from: "showFotocadeauCta",
        to:   ".$.overlayRight.showFotocadeauCta"
    }, {
        from: "showDescription",
        to:   ".$.overlayRight.showDescription"
    }, {
        from: "useOverlayLeft",
        to:   ".$.overlayRight.useOverlayLeft"
    }],

    /**
     * @private
     */
    observers: {
        update:                  ["state", "useOverlayLeft"],
        updateOverlayVisibility: ["useOverlayLeft", "showFotocadeauCta", "showDescription"]
    },

    /**
     * @private
     */
    handlers: {
        onRequestZoomOut:           "requestZoomOut",
        onRequestZoomIn:            "requestZoomIn",
        onRequestPreviousImage:     "requestPreviousImage",
        onRequestNextImage:         "requestNextImage",
        onRequestFullscreen:        "requestFullscreen",
        onRequestExitFullscreen:    "cancelFullscreen",
        onRequestToggleDescription: "toggleDescription",
        onRequestLongDescription:   "readMore"
    },

    /**
     * @private
     */
    create: kind.inherit(function(sup) {
        return function() {
            this.update = frameThrottle(this.bindSafely(this.update));
            sup.apply(this, arguments);
            this.observe("app.$.i18nService.translation", this.update);
        };
    }),

    /**
     * @private
     */
    update: function() {
        var state           = this.state || {},
            mediaItem       = get(state, "media", null),
            asset           = get(state, "asset", null),
            watchingSession = get(state, "watchingSession", null);

        // Insufficient data, revert to not ready state
        if (!mediaItem || !asset || !watchingSession) {
            this.clearViewer();
            this.resetPlayerState();

            return;
        }

        // Only activate when absoluteShowing
        if (!this.absoluteShowing) {
            this.addToAbsoluteShowingQueue("update");

            return;
        }
        this.removeFromAbsoluteShowingQueue("update");

        // Ok, lets go
        this.resetPlayerState();
        this.set("isReady", 0);

        // Apply viewer content
        this.hydrateViewer();

        // Set isReady state
        this.set("isPlaying", true); // TODO: Whenever the image is loaded
        this.set("isReady", 1);
    },

    /**
     * @private
     */
    clearViewer: function() {
        var $canvasView          = this.$.canvasView,
            $previousImageButton = this.$.previousImageButton,
            $nextImageButton     = this.$.nextImageButton;

        // Turn off Fotocadeau UI
        this.set("showFotocadeauCta", false);

        // Clear description
        this.$.overlayLeft.set("state", null);
        this.$.overlayRight.set("state", null);
        this.set("showDescription", false);

        // Clear canvas
        $canvasView.set("data", null);

        // Clear watching session
        $canvasView.set("watchingSession", null);

        // Hide nav buttons
        $previousImageButton.hide();
        $previousImageButton.set("tooltip", "");
        $nextImageButton.hide();
        $nextImageButton.set("tooltip", "");
    },

    /**
     * @private
     */
    hydrateViewer: function() {
        var i18nService          = this.app.$.i18nService,
            authService          = this.app.$.authService,
            state                = this.state || {},
            mediaItem            = get(state, "media", null),
            asset                = get(state, "asset", null),
            watchingSession      = get(state, "watchingSession", null),
            allowBuyButton       = get(config, "fotocadeau.buyButton", false),
            isFotocadeauEnabled  = mediaItem && get(mediaItem, "photo_print_enabled", false),
            $overlayLeft         = this.$.overlayLeft,
            $overlayRight        = this.$.overlayRight,
            $canvasView          = this.$.canvasView,
            $previousImageButton = this.$.previousImageButton,
            $nextImageButton     = this.$.nextImageButton;

        // Fotocadeau UI toggle
        if (!allowBuyButton || !isFotocadeauEnabled || authService.isLoggedIn !== 1) {
            this.set("showFotocadeauCta", false);
        }
        else {
            this.set("showFotocadeauCta", true);
        }

        // Apply description
        $overlayLeft.set("state", state);
        $overlayRight.set("state", state);

        // Change asset as needed
        if ($canvasView.get("data") !== asset) {
            $canvasView.set("data", asset);

            // Only auto-open short description when we actually change assets
            if (get(state, "category.category_layout.show_overlay", false)) {
                this.set("showDescription", true);
            }
        }

        // Reset watching session
        if ($canvasView.get("watchingSession") !== watchingSession) {
            $canvasView.set("watchingSession", watchingSession);
        }

        // Nav button visibility
        if (get(state, "watchingSession.prev.type", null) === "photo") {
            $previousImageButton.set("tooltip", i18nService.getString("app.MediaPlayer.ImageViewer.PreviousImageButton.tooltip"));
            $previousImageButton.show();
        }
        else {
            $previousImageButton.hide();
            $previousImageButton.set("tooltip", "");
        }

        if (get(state, "watchingSession.next.type", null) === "photo") {
            $nextImageButton.set("tooltip", i18nService.getString("app.MediaPlayer.ImageViewer.NextImageButton.tooltip"));
            $nextImageButton.show();
        }
        else {
            $nextImageButton.hide();
            $nextImageButton.set("tooltip", "");
        }
    },

    /**
     * Verify that this player can be (re)used to play the supplied media.
     *
     * @param    {?Object}  mediaData  The media data
     * @returns  {boolean}  Whether this tech can play the supplied media
     * @public
     */
    isReusableFor: function(mediaData) {
        var mediaType = get(mediaData, "type", null);

        return this.isReusable
            && mediaType === "photo";
    },

    /**
     * We use css flags as breakpoints because the viewer might be minified, in
     * which case it's size is influenced by screen size.
     *
     * @private
     */
    reflow: kind.inherit(function(sup) {
        return function() {
            var viewerBounds,
                viewerWidth;

            sup.apply(this, arguments);
            viewerBounds = this.getBounds();
            viewerWidth  = viewerBounds.width;

            if (viewerWidth < 375) {
                this.setAttribute("data-break-point", "tiny");
            }
            else if (viewerWidth >= 375 && viewerWidth < 480) {
                this.setAttribute("data-break-point", "small");
            }
            else if (viewerWidth >= 480 && viewerWidth < 960) {
                this.setAttribute("data-break-point", "medium");
            }
            else if (viewerWidth >= 960) {
                this.setAttribute("data-break-point", "large");
            }

            this.set("useOverlayLeft", viewerWidth >= 768);
        };
    }),

    // Viewer Key Commands
    // --------------------------------

    /**
     * Use keyboard keys to control the viewer.
     *
     * @param  {enyo.Control}  sender  The sender
     * @param  {Object}        event   The event
     * @private
     */
    handleKeyUp: function(sender, event) {
        // Do nothing if the event was already processed
        if (event.defaultPrevented) {
            return;
        }

        // Previous image
        if (event.key === "Left" || event.key === "ArrowLeft") {
            this.requestPreviousImage(sender, event);
        }

        // Next image
        if (event.key === "Right" || event.key === "ArrowRight") {
            this.requestNextImage(sender, event);
        }

        // Zoom in
        if (event.key === "Add" || event.key === "+") {
            this.requestZoomIn(sender, event);
        }

        // Zoom out
        if (event.key === "Subtract" || event.key === "-") {
            this.requestZoomOut(sender, event);
        }
    },

    // Viewer UI Commands
    // --------------------------------

    /**
     * Forward the zoom event to the canvas.
     *
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop propagation
     * @private
     */
    requestZoomOut: function(sender, event) {
        if (this.isReady === 1) {
            this.$.canvasView.waterfallDown("onRequestZoomOut", event);
        }

        return true;
    },

    /**
     * Forward the zoom event to the canvas.
     *
     * @param    {enyo.Control}  sender  The sender
     * @param    {Object}        event   The event
     * @returns  {boolean}       Stop propagation
     * @private
     */
    requestZoomIn: function(sender, event) {
        if (this.isReady === 1) {
            this.$.canvasView.waterfallDown("onRequestZoomIn", event);
        }

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    requestPreviousImage: function() {
        var canvasView = this.$.canvasView,
            state      = this.get("state") || {},
            prevMedia  = get(state, "prevMedia", null),
            prevType   = get(prevMedia, "type", null);

        if (this.isReady === 1 && prevType === "photo") {
            canvasView.set("direction", "backward");
        }

        this.app.$.mediaPlayerManager.autoNavToMediaItem(prevMedia);

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    requestNextImage: function() {
        var canvasView = this.$.canvasView,
            state      = this.get("state") || {},
            nextMedia  = get(state, "nextMedia", null),
            nextType   = get(nextMedia, "type", null);

        if (this.isReady === 1 && nextType === "photo") {
            canvasView.set("direction", "forward");
        }

        this.app.$.mediaPlayerManager.autoNavToMediaItem(nextMedia);

        return true;
    },

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    requestFullscreen: kind.inherit(function(sup) {
        return function() {
            if (this.isReady === 1) {
                sup.apply(this, arguments);

                // Fallback full-screen mode doesn't produce any events, so we
                // produce one on our own.
                if (!Control.Fullscreen.nativeSupport()) {
                    Signals.send("onFullscreenChange");
                }
            }

            return true;
        };
    }),

    /**
     * @returns  {boolean}  Stop propagation
     * @private
     */
    cancelFullscreen: kind.inherit(function(sup) {
        return function() {
            if (this.isReady === 1) {
                sup.apply(this, arguments);

                // Fallback full-screen mode doesn't produce any events, so we
                // produce one on our own.
                if (!Control.Fullscreen.nativeSupport()) {
                    Signals.send("onFullscreenChange");
                }
            }

            return true;
        };
    }),

    // Description
    // --------------------------------

    /**
     * @private
     */
    toggleDescription: function() {
        this.set("showDescription", !this.get("showDescription"));
    },

    /**
     * @private
     */
    readMore: function() {
        var $longDescription = this.$.longDescription;

        if (!$longDescription) {
            $longDescription = this.createComponent({
                name:     "longDescription",
                kind:     LongDescription,
                floating: false,
                state:    this.state || {}
            });
            $longDescription.render();
            $longDescription.requestShow(true, false);
            $longDescription.on("hidden", this.bindSafely(function() {
                $longDescription.destroy();
            }));
        }
    },

    /**
     * @private
     */
    updateOverlayVisibility: function() {
        var useOverlayLeft    = this.get("useOverlayLeft"),
            showDescription   = this.get("showDescription"),
            showFotocadeauCta = this.get("showFotocadeauCta"),
            $overlayLeft      = this.$.overlayLeft,
            $overlayRight     = this.$.overlayRight;

        if (useOverlayLeft && showDescription) {
            $overlayLeft.showDropdown();
        }
        else {
            $overlayLeft.hideDropdown();
        }

        if ((!useOverlayLeft && showDescription) || showFotocadeauCta) {
            $overlayRight.showDropdown();
        }
        else {
            $overlayRight.hideDropdown();
        }
    },

    // Viewer API
    // --------------------------------

    /**
     * Hide the controls.
     *
     * @returns  {boolean}  `true` if hidden, `false` otherwise.
     * @public
     */
    hideControls: function() {
        this.$.toolbar.hide();
        this.set("showDescription", false);
        this.set("showFotocadeauCta", false);

        return true;
    },

    /**
     * Show the controls.
     *
     * @returns  {boolean}  `true` if showing, `false` otherwise.
     * @public
     */
    showControls: function() {
        this.$.toolbar.show();
        this.set("showFotocadeauCta", true);

        // Will reveal the short description if needed
        this.update();

        return true;
    }

});
